import { getExperimentValue, isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import {
  composePastePayloadFromClipboardItem,
  pasteOrDropEventCaptureListener,
} from '@canvas/clipboard';
import {
  type ClipboardPermissionResult,
  hasAdequatePermission,
} from '@canvas/clipboard/permission';
import { toError } from '@canvas/error';
import { type CanvasHTMLElement } from '@canvas/input-types/base-event-types';
import { type SyntheticStampPickedCapture } from '@canvas/input-types/synthetic-event-types';
import {
  type CanvasUIButtonEvent,
  type CanvasCopyEvent,
  type CanvasCutEvent,
  type CanvasCreateCommentEvent,
  type CanvasDeleteEvent,
  type CanvasExportEvent,
  type CanvasImportEvent,
  type CanvasPasteEvent,
  type CanvasStampPickedEvent,
} from '@canvas/model/ui';

import { getInputTarget } from '../input-target/get-input-target.ts';
import {
  type CaptureType,
  type CaptureListener,
  type IdCallback,
  type Capture,
} from '../input-types.ts';

import { readClipboardSafely } from './read-clipboard-safely.ts';

export const setupSyntheticEvents = (
  root: CanvasHTMLElement,
  id: IdCallback,
  onCapture: (capture: Capture) => void,
  captureContext: {
    x?: number;
    y?: number;
    clipboardReadPermission: ClipboardPermissionResult;
    clipboardWritePermission: ClipboardPermissionResult;
  },
  listeners: Map<CaptureType, CaptureListener>,
) => {
  const onCutSynthetic = async (evt: CanvasCutEvent) => {
    const copied = await onCopySynthetic(evt);
    if (!copied) {
      return;
    }

    onCapture({
      id: id(),
      type: 'delete',
      target: getInputTarget(evt.target),
      properties: {},
    });
  };

  const onCopySynthetic = async (_evt: CanvasCopyEvent): Promise<boolean> => {
    if (hasAdequatePermission(captureContext.clipboardWritePermission)) {
      const payload = await listeners.get('copy')?.();
      if (!payload) {
        return false;
      }

      const type = 'text/html';
      const blob = new Blob([payload], { type });
      const data = [new ClipboardItem({ [type]: blob })];

      return new Promise((resolve) => {
        navigator.clipboard.write(data).then(
          () => resolve(true),
          () => resolve(false),
        );
      });
    }
    return false;
  };

  const onPasteSynthetic = async (evt: CanvasPasteEvent) => {
    const pastePosition = { x: captureContext.x, y: captureContext.y };
    const capturePositionProperties = {
      x: pastePosition.x ?? globalThis.innerWidth / 2,
      y: pastePosition.y ?? globalThis.innerHeight / 2,
    };
    if (hasAdequatePermission(captureContext.clipboardReadPermission)) {
      const data = await readClipboardSafely();
      if (data instanceof Error) {
        return onCapture({
          id: id(),
          type: 'pasteError',
          target: getInputTarget(evt.target),
          properties: {
            error: data,
            ...capturePositionProperties,
          },
        });
      }

      const isImprovedCopyPasteFlagEnabled = isFeatureFlagEnabled(
        'improvedCopyPasteFlag',
      );
      let isImprovedCopyPasteGateEnabled = false;
      // We only want the user to have access to the new function if the flag is enabled AND
      // the user is a part of the experiment. We also only want to fire the experiment
      // exposure event if the feature flag is enabled.
      if (isImprovedCopyPasteFlagEnabled) {
        isImprovedCopyPasteGateEnabled = getExperimentValue(
          'improvedCopyPasteGate',
          'isEnabled',
          {
            fireExperimentExposure: true,
          },
        );
      }

      if (isImprovedCopyPasteGateEnabled) {
        try {
          const payload = await pasteOrDropEventCaptureListener(data);
          if (!payload) {
            return;
          }
          onCapture({
            id: id(),
            type: 'paste',
            target: getInputTarget(evt.target),
            properties: {
              clipboardContent: payload,
              ...capturePositionProperties,
            },
          });
        } catch (error) {
          return onCapture({
            id: id(),
            type: 'pasteError',
            target: getInputTarget(evt.target),
            properties: {
              error: toError(error),
              ...capturePositionProperties,
            },
          });
        }
      } else {
        for (let i = 0; i < data.length; i++) {
          const payload = await composePastePayloadFromClipboardItem(data[i]);
          if (!payload) {
            return;
          }

          onCapture({
            id: id(),
            type: 'paste',
            target: getInputTarget(evt.target),
            properties: {
              clipboardContent: payload,
              ...capturePositionProperties,
            },
          });
        }
      }
    }
  };

  const onExportSynthetic = async (evt: CanvasExportEvent) => {
    onCapture({
      id: id(),
      type: 'export',
      target: getInputTarget(evt.target),
      properties: {},
    });
  };

  const onImportSynthetic = async (evt: CanvasImportEvent) => {
    onCapture({
      id: id(),
      type: 'import',
      target: getInputTarget(evt.target),
      properties: {},
    });
  };

  const onUiButtonEvent = async (evt: CanvasUIButtonEvent) => {
    onCapture({
      id: id(),
      type: 'uiButtonEvent',
      target: getInputTarget(evt.target),
      properties: evt.detail,
    });
  };

  const onDeleteSynthetic = async (evt: CanvasDeleteEvent) => {
    onCapture({
      id: id(),
      type: 'delete',
      target: getInputTarget(evt.target),
      properties: {},
    });
  };

  const onStampSynthetic = (evt: CanvasStampPickedEvent) => {
    const target = getInputTarget(evt.target);
    const capture: SyntheticStampPickedCapture = {
      id: id(),
      type: 'stampPicked',
      target,
      properties: evt.detail,
    };

    onCapture(capture);
  };

  const onSidebarSelectOpenSynthetic = () => {
    onCapture({
      id: id(),
      type: 'sidebarSelectOpen',
      target: 'configure-action-menu',
      properties: {},
    });
  };

  const onSidebarSelectCloseSynthetic = () => {
    onCapture({
      id: id(),
      type: 'sidebarSelectClose',
      target: 'configure-action-menu',
      properties: {},
    });
  };

  const onCreateCommentSynthetic = (evt: CanvasCreateCommentEvent) => {
    onCapture({
      id: id(),
      type: 'createComment',
      target: getInputTarget(evt.target),
      properties: evt.detail,
    });
  };

  root.addEventListener('canvas:delete', onDeleteSynthetic);
  root.addEventListener('canvas:uiButtonEvent', onUiButtonEvent);
  root.addEventListener('canvas:copy', onCopySynthetic);
  root.addEventListener('canvas:cut', onCutSynthetic);
  root.addEventListener('canvas:paste', onPasteSynthetic);
  root.addEventListener('canvas:create-comment', onCreateCommentSynthetic);
  root.addEventListener('canvas:export', onExportSynthetic);
  root.addEventListener('canvas:import', onImportSynthetic);
  root.addEventListener('canvas:stamp', onStampSynthetic);
  root.addEventListener('canvas:sidebar-select:open', onSidebarSelectOpenSynthetic);
  root.addEventListener('canvas:sidebar-select:close', onSidebarSelectCloseSynthetic);

  return () => {
    root.removeEventListener('canvas:delete', onDeleteSynthetic);
    root.removeEventListener('canvas:uiButtonEvent', onUiButtonEvent);
    root.removeEventListener('canvas:copy', onCopySynthetic);
    root.removeEventListener('canvas:cut', onCutSynthetic);
    root.removeEventListener('canvas:paste', onPasteSynthetic);
    root.removeEventListener('canvas:stamp', onStampSynthetic);
    root.removeEventListener('canvas:export', onExportSynthetic);
    root.removeEventListener('canvas:import', onImportSynthetic);
    root.removeEventListener('canvas:sidebar-select:open', onSidebarSelectOpenSynthetic);
    root.removeEventListener(
      'canvas:sidebar-select:close',
      onSidebarSelectCloseSynthetic,
    );
  };
};
